import {
  ONBOARDING_OBJECTIVE,
  ONBOARDING_STEP,
  type OnboardingDraft,
  type OnboardingFinalCta,
  type OnboardingObjective,
  type OnboardingStepId,
  type OnboardingSurveyProfile,
} from '@/types/onboarding'

const OBJECTIVE_SUFFIX: Record<OnboardingObjective, OnboardingStepId[]> = {
  [ONBOARDING_OBJECTIVE.planProjects]: [
    ONBOARDING_STEP.teamName,
    ONBOARDING_STEP.projectPlace,
    ONBOARDING_STEP.saleStatus,
    ONBOARDING_STEP.final,
  ],
  [ONBOARDING_OBJECTIVE.visibility]: [
    ONBOARDING_STEP.shopName,
    ONBOARDING_STEP.saleStatus,
    ONBOARDING_STEP.final,
  ],
  [ONBOARDING_OBJECTIVE.resources]: [
    ONBOARDING_STEP.resourceStart,
    ONBOARDING_STEP.final,
  ],
}

// Keep only the information needed to choose a useful first action. Team size,
// job and detailed usage can be collected later without blocking access.
export const resolveCommonPrefix = (
  _surveyProfile?: OnboardingSurveyProfile,
): OnboardingStepId[] => [ONBOARDING_STEP.profile, ONBOARDING_STEP.objective]

export const resolveOnboardingSteps = (
  objective: OnboardingObjective | undefined,
  surveyProfile?: OnboardingSurveyProfile,
): OnboardingStepId[] => {
  const prefix = resolveCommonPrefix(surveyProfile)
  if (!objective) {
    return prefix
  }
  return [...prefix, ...OBJECTIVE_SUFFIX[objective]]
}

export const getOnboardingStepAt = (
  objective: OnboardingObjective | undefined,
  index: number,
  surveyProfile?: OnboardingSurveyProfile,
): OnboardingStepId | undefined => {
  const steps = resolveOnboardingSteps(objective, surveyProfile)
  return steps[index]
}

export const getOnboardingStepIndex = (
  objective: OnboardingObjective | undefined,
  stepId: OnboardingStepId,
  surveyProfile?: OnboardingSurveyProfile,
): number => {
  const steps = resolveOnboardingSteps(objective, surveyProfile)
  return steps.indexOf(stepId)
}

export const getOnboardingProgressSteps = (
  objective: OnboardingObjective | undefined,
  surveyProfile?: OnboardingSurveyProfile,
): OnboardingStepId[] => {
  const steps = resolveOnboardingSteps(objective, surveyProfile)
  return steps.filter((id) => id !== ONBOARDING_STEP.final)
}

export const getOnboardingStepIndicator = (
  stepId: OnboardingStepId,
  objective: OnboardingObjective | undefined,
  surveyProfile: OnboardingSurveyProfile | undefined,
  t: (
    key: 'step-indicator',
    values: { current: number; total: number },
  ) => string,
): string | undefined => {
  const prefix = resolveCommonPrefix(surveyProfile)
  const prefixIndex = prefix.indexOf(stepId)
  if (prefixIndex >= 0) {
    return t('step-indicator', {
      current: prefixIndex + 1,
      total: prefix.length,
    })
  }

  const progressSteps = getOnboardingProgressSteps(objective, surveyProfile)
  const progressIndex = progressSteps.indexOf(stepId)
  if (progressIndex < 0) {
    return undefined
  }

  return t('step-indicator', {
    current: progressIndex + 1,
    total: progressSteps.length,
  })
}

export const getCommonStepIndicator = (
  stepId: OnboardingStepId,
  surveyProfile: OnboardingSurveyProfile | undefined,
  t: (
    key: 'step-indicator',
    values: { current: number; total: number },
  ) => string,
): string | undefined => {
  return getOnboardingStepIndicator(stepId, undefined, surveyProfile, t)
}

export const resolveFinalCtas = (
  draft: OnboardingDraft,
): OnboardingFinalCta[] => {
  switch (draft.objective) {
    case ONBOARDING_OBJECTIVE.planProjects:
      return [
        {
          id: 'import-resources',
          href: '/projects',
          labelKey: 'final-cta-import-resources',
          variant: 'secondary',
        },
        {
          id: 'open-project',
          href: '/projects',
          labelKey: 'final-cta-create-project',
          variant: 'primary',
        },
      ]
    case ONBOARDING_OBJECTIVE.visibility:
      return [
        {
          id: 'import-resources',
          href: '/inventory/import',
          labelKey: 'final-cta-import-resources',
          variant: 'secondary',
        },
        {
          id: 'configure-shop',
          href: '/shop/settings/profile',
          labelKey: 'final-cta-configure-shop',
          variant: 'primary',
        },
      ]
    case ONBOARDING_OBJECTIVE.resources:
    default: {
      const isImport = draft.startMode === 'import'
      return [
        {
          id: 'import-stock',
          href: '/inventory/import',
          labelKey: 'final-cta-import-stock',
          variant: isImport ? 'primary' : 'secondary',
        },
        {
          id: 'create-resource',
          href: '/inventory',
          labelKey: 'final-cta-create-resource',
          variant: isImport ? 'secondary' : 'primary',
        },
      ]
    }
  }
}
